import styled from "styled-components";
import { Box, Flex, Text } from "rebass";
import { HiCheck, HiArchive } from "react-icons/hi";
import { PrimaryButton } from "../styledComponents";
import { IStorageStatus } from "../@types";

interface SuccessProps {
  clipTitle: string;
  clipThumbnail?: string;
  storageStatus: IStorageStatus;
  onDone: () => void;
}

export const Success = ({
  clipTitle,
  clipThumbnail,
  storageStatus,
  onDone,
}: SuccessProps) => {
  const percentage = (storageStatus.savedCount / storageStatus.limit) * 100;

  return (
    <>
      <Box p="24px 28px">
        <ClipPreview mb="24px">
          <ThumbnailWrapper>
            {clipThumbnail ? (
              <Thumbnail src={clipThumbnail} alt={clipTitle} />
            ) : (
              <ThumbnailPlaceholder>
                <HiArchive size={24} />
              </ThumbnailPlaceholder>
            )}
            <SavedBadge>
              <HiCheck size={12} />
            </SavedBadge>
          </ThumbnailWrapper>
          <ClipInfo>
            <ClipTitle>{clipTitle}</ClipTitle>
            <SavedText>
              <HiCheck size={14} />
              <span>Saved to collection</span>
            </SavedText>
          </ClipInfo>
        </ClipPreview>

        <Box>
          <StorageHeader mb="10px">
            <StorageLabel>Your Storage</StorageLabel>
            <StorageCount>
              <EnvyCount>{storageStatus.savedCount}</EnvyCount> /{" "}
              {storageStatus.limit} clips
            </StorageCount>
          </StorageHeader>
          <ProgressBar>
            <ProgressFill $percentage={percentage} />
          </ProgressBar>
        </Box>
      </Box>

      <ModalFooter>
        <PrimaryButton onClick={onDone}>Done</PrimaryButton>
      </ModalFooter>
    </>
  );
};

const ModalFooter = styled(Flex)`
  border-top: 1px solid ${({ theme }) => theme.colors.navy50};
  gap: 12px;
  justify-content: flex-end;
  padding: 16px 28px;
`;

const ClipPreview = styled(Flex)`
  align-items: center;
  background: ${({ theme }) => theme.colors.navy};
  border-radius: 12px;
  gap: 16px;
  padding: 14px;
`;

const ThumbnailWrapper = styled(Box)`
  flex-shrink: 0;
  position: relative;
`;

const Thumbnail = styled.img`
  border-radius: 8px;
  display: block;
  height: 70px;
  object-fit: cover;
  width: 124px;
`;

const ThumbnailPlaceholder = styled(Flex)`
  align-items: center;
  background: ${({ theme }) => theme.colors.navy50};
  border-radius: 8px;
  color: ${({ theme }) => theme.colors.shade400};
  height: 70px;
  justify-content: center;
  width: 124px;
`;

const SavedBadge = styled(Flex)`
  align-items: center;
  background: ${({ theme }) => theme.colors.envy};
  border-radius: 50%;
  bottom: -4px;
  color: ${({ theme }) => theme.colors.ice};
  height: 22px;
  justify-content: center;
  position: absolute;
  right: -4px;
  width: 22px;
`;

const ClipInfo = styled(Box)`
  flex: 1;
  min-width: 0;
`;

const ClipTitle = styled(Text)`
  color: ${({ theme }) => theme.colors.ice};
  font-family: Barlow;
  font-size: 15px;
  font-weight: 600;
  margin-bottom: 6px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const SavedText = styled(Flex)`
  align-items: center;
  color: ${({ theme }) => theme.colors.envy};
  font-family: Barlow;
  font-size: 13px;
  font-weight: 500;
  gap: 6px;
`;

const StorageHeader = styled(Flex)`
  align-items: center;
  justify-content: space-between;
`;

const StorageLabel = styled(Text)`
  color: ${({ theme }) => theme.colors.shade200};
  font-family: Barlow;
  font-size: 14px;
  font-weight: 500;
`;

const StorageCount = styled(Text)`
  color: ${({ theme }) => theme.colors.ice};
  font-family: Barlow;
  font-size: 18px;
  font-weight: 600;
`;

const EnvyCount = styled.span`
  color: ${({ theme }) => theme.colors.envy};
`;

const ProgressBar = styled(Box)`
  background: ${({ theme }) => theme.colors.navy};
  border-radius: 9px;
  height: 18px;
  overflow: hidden;
  width: 100%;
`;

const ProgressFill = styled(Box)<{ $percentage: number }>`
  background: ${({ theme, $percentage }) => {
    if ($percentage >= 90) return theme.colors.alert;
    if ($percentage >= 70) return theme.colors.caution;
    return theme.colors.envy;
  }};
  border-radius: 9px;
  height: 100%;
  transition: width 0.3s ease;
  width: ${({ $percentage }) => Math.min($percentage, 100)}%;
`;
